// Format utilities - used across all components

export function formatDate(d) {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return d;
  }
}

// Date AND time. For anything where the ordering within a day matters — two
// invoices from the same supplier arriving the same morning, which email came
// in first — a date alone is not enough to tell them apart.
export function formatDateTime(d) {
  if (!d) return '-';
  try {
    const dt = new Date(d);
    return `${dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}, `
         + `${dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
  } catch {
    return d;
  }
}

// Just the time, for a row that already says which day it is.
export function formatTime(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  } catch {
    return '';
  }
}

export function formatDateLong(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

// Currencies a purchase order can be raised in (mirrors PO_CURRENCIES in the API).
export const CURRENCIES = ['GBP', 'EUR', 'USD'];
const CURRENCY_SYMBOLS = { GBP: '£', EUR: '€', USD: '$' };

export function currencySymbol(code) {
  return CURRENCY_SYMBOLS[String(code || 'GBP').toUpperCase()] || '';
}

// Amounts are held in their own currency and never converted, so pass the
// record's currency wherever it isn't sterling. Grouping stays en-GB: it's a
// UK reader looking at a supplier's figure, not localised output.
export function formatMoney(v, dp = 2, currency = 'GBP') {
  if (v == null || v === '' || isNaN(v)) return '-';
  const code = String(currency || 'GBP').toUpperCase();
  const sym = CURRENCY_SYMBOLS[code];
  const amount = Number(v).toLocaleString('en-GB', { minimumFractionDigits: dp, maximumFractionDigits: dp });
  return sym ? sym + amount : `${code} ${amount}`;
}

export function formatPercent(v) {
  if (v == null || v === '' || isNaN(v)) return '-';
  return Number(v).toFixed(1) + '%';
}

export function initials(name) {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

export function fullName(person) {
  if (!person) return 'Unknown';
  return `${person.first_name} ${person.last_name}`.trim();
}

export function daysAgo(dateStr) {
  if (!dateStr) return null;
  const diff = Math.floor((new Date() - new Date(dateStr)) / 86400000);
  if (diff === 0) return 'today';
  if (diff === 1) return 'yesterday';
  return `${diff} days ago`;
}

// Sanitise a user-supplied URL for use in href: allow http(s), app-relative
// paths, mailto and tel — block javascript:, data:, etc. Returns null when
// the URL is unsafe so callers can skip rendering the link.
export function safeUrl(u) {
  if (!u || typeof u !== 'string') return null;
  const t = u.trim();
  if (/^(https?:\/\/|\/|mailto:|tel:)/i.test(t)) return t;
  return null;
}
